import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiClient, auth } from "@/lib/api";
import { TIDS } from "@/lib/testIds";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { EditEmployeeDialog } from "@/components/EditEmployeeDialog";
import { toast } from "sonner";
import { ChevronLeft, Mail, Phone, KeyRound, Users, ArrowRight, Pencil } from "lucide-react";

export default function AdminEmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const fetchEmp = async () => {
    try {
      const res = await apiClient.get(`/admin/employees/${id}`);
      setEmp(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load employee");
      if (err?.response?.status === 404) navigate("/admin/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.getRole() !== "admin") {
      navigate("/admin/login");
      return;
    }
    fetchEmp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  if (loading) {
    return (
      <DashboardShell role="admin" title="Employee">
        <div className="p-10 text-center text-slate-500">Loading…</div>
      </DashboardShell>
    );
  }

  if (!emp) return null;

  return (
    <DashboardShell
      role="admin"
      title={emp.full_name}
      subtitle="Referral partners this employee recruited, and the deals they've brought in."
      actions={
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setEditOpen(true)}
            data-testid={TIDS.adminEmployeeEdit + "-detail"}
            className="rounded-full bg-blue-600 hover:bg-blue-700"
          >
            <Pencil className="h-4 w-4 mr-1" /> Edit
          </Button>
          <Link to="/admin/dashboard">
            <Button variant="outline" className="rounded-full" data-testid={TIDS.adminEmployeeBack}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          </Link>
        </div>
      }
    >
      <div data-testid={TIDS.adminEmployeeDetail} className="space-y-8">
        <Breadcrumbs
          items={[
            { label: "Employees", to: "/admin/dashboard" },
            { label: emp.full_name },
          ]}
        />
        {/* Employee profile card */}
        <Card className="rounded-2xl border-slate-200 shadow-sm p-6 md:p-7">
          <div className="grid sm:grid-cols-3 gap-5">
            <div className="flex items-start gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                <Mail className="h-4 w-4" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Email</div>
                <div className="text-sm text-slate-900 break-all">{emp.email}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                <Phone className="h-4 w-4" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Mobile</div>
                <div className="text-sm text-slate-900">{emp.mobile || <span className="text-slate-400">—</span>}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
                <KeyRound className="h-4 w-4" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Recruit code</div>
                <div className="text-sm font-mono text-slate-900">{emp.recruit_code}</div>
              </div>
            </div>
          </div>
        </Card>

        {/* Partners list */}
        <Card className="rounded-xl border-slate-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                <Users className="h-4 w-4" />
              </span>
              <h2 className="text-lg font-medium text-slate-900">Referral partners ({emp.partners?.length || 0})</h2>
            </div>
          </div>
          {(emp.partners || []).length === 0 ? (
            <div className="p-10 text-center text-slate-500">No partners recruited yet by this employee.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead className="text-right">Clients</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {emp.partners.map((p) => (
                  <TableRow key={p.id} data-testid={TIDS.adminEmpPartnerRow} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/partners/${p.id}`)}>
                    <TableCell className="font-medium text-slate-900">{p.full_name}</TableCell>
                    <TableCell className="text-slate-700">{p.email}</TableCell>
                    <TableCell className="text-slate-700">{p.mobile}</TableCell>
                    <TableCell className="text-slate-700">{p.company_name || "—"}</TableCell>
                    <TableCell className="text-right font-medium">{p.referral_count}</TableCell>
                    <TableCell className="text-slate-500 text-sm">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="hover:text-blue-700" onClick={(e) => { e.stopPropagation(); navigate(`/admin/partners/${p.id}`); }}>
                        View clients <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <EditEmployeeDialog
        employee={emp}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={fetchEmp}
      />
    </DashboardShell>
  );
}
